import { createAsyncThunk } from "@reduxjs/toolkit";
import localhost, { downloadFileFromResponse } from "apis/localhost";
import mapActivityLogEntry from "slices/activityLog/mapActivityLogEntry";
import { showDownloadErrorModal } from "slices/app/actions/error";
import { getErrorMessage } from "utils";

import { selectIsCustomRangeIncomplete } from "./selectors";

const BASE_URL = "/api/activity/summary";
const ACTIVITY_URL = "/api/activity/summary/log";
const BY_AREA_FILE_NAME = "usage-stats-by-area.csv";
const BY_USER_FILE_NAME = "usage-stats-by-user.csv";
const LOG_FILE_NAME = "usage-stats-log.csv";
const CSV_MIME_TYPE = "text/csv";
const VISI_CHATS_URL = "/visi/everywhere/chat-history";
const ALL_VISI_CHATS_URL = "/api/visi/chat-history";
const VISI_CHATS_FILE_NAME = "visi-chat-history.csv";
const AI_USAGE_URL = "/api/activity/summary/llm-usage";
const AI_USAGE_FILE_NAME = "usage-stats-ai-usage.csv";
const TOKEN_USAGE_URL = `${AI_USAGE_URL}/timeline`;

export const ACTIVITY_PAGE_SIZE = 50;

const buildParams = ({
  range,
  from,
  to,
  source,
  sort,
  q,
  user,
  granularity,
} = {}) => {
  const params = {};
  if (range) {
    params.range = range;
  }
  if (from) {
    params.from = from;
  }
  if (to) {
    params.to = to;
  }
  if (source) {
    params.source = source;
  }
  if (sort) {
    params.sort = sort;
  }
  if (q) {
    params.q = q;
  }
  if (user) {
    params.user = user;
  }
  if (granularity) {
    params.granularity = granularity;
  }
  return params;
};

export const fetchByArea = createAsyncThunk(
  "usageStats/fetchByArea",
  async (_, { getState }) => {
    const { filters } = getState().usageStats;
    const { data } = await localhost.get(`${BASE_URL}/by-area`, {
      params: buildParams(filters),
    });
    return data;
  }
);

export const fetchByUser = createAsyncThunk(
  "usageStats/fetchByUser",
  async ({ sort = "total" } = {}, { getState }) => {
    const { filters } = getState().usageStats;
    const { data } = await localhost.get(`${BASE_URL}/by-user`, {
      params: buildParams({ ...filters, sort }),
    });
    return data;
  }
);

export const fetchActivityFeed = createAsyncThunk(
  "usageStats/fetchActivityFeed",
  async ({ offset = 0, pageSize = ACTIVITY_PAGE_SIZE } = {}, { getState }) => {
    const { activitySearch, activitySort } = getState().usageStats;
    const { data } = await localhost.get(ACTIVITY_URL, {
      params: {
        offset,
        pageSize,
        sort: activitySort.field,
        order: activitySort.direction,
        ...(activitySearch ? { q: activitySearch } : {}),
      },
    });
    return {
      offset,
      rows: data.rows.map(mapActivityLogEntry),
      total: data.total,
      filteredTotal: data.filteredTotal,
    };
  }
);

export const exportByAreaThunk = createAsyncThunk(
  "usageStats/exportByArea",
  async (_, { getState }) => {
    const { filters } = getState().usageStats;
    const response = await localhost.get(`${BASE_URL}/by-area/export`, {
      params: buildParams(filters),
      responseType: "blob",
    });
    downloadFileFromResponse(response.data, BY_AREA_FILE_NAME, {
      type: CSV_MIME_TYPE,
    });
  }
);

export const exportByUserThunk = createAsyncThunk(
  "usageStats/exportByUser",
  async (_, { getState }) => {
    const { filters } = getState().usageStats;
    const response = await localhost.get(`${BASE_URL}/by-user/export`, {
      params: buildParams(filters),
      responseType: "blob",
    });
    downloadFileFromResponse(response.data, BY_USER_FILE_NAME, {
      type: CSV_MIME_TYPE,
    });
  }
);

export const exportLogThunk = createAsyncThunk(
  "usageStats/exportLog",
  async (_, { getState }) => {
    const { activitySearch, activitySort } = getState().usageStats;
    const response = await localhost.get(`${ACTIVITY_URL}/export`, {
      params: {
        sort: activitySort.field,
        order: activitySort.direction,
        ...(activitySearch ? { q: activitySearch } : {}),
      },
      responseType: "blob",
    });
    downloadFileFromResponse(response.data, LOG_FILE_NAME, {
      type: CSV_MIME_TYPE,
    });
  }
);

const visiChatsUrl = ({ includeWriterChats } = {}) =>
  includeWriterChats ? ALL_VISI_CHATS_URL : VISI_CHATS_URL;

const visiChatParams = ({ query, area, sort } = {}) => ({
  ...(query?.trim() ? { q: query.trim() } : {}),
  ...(area ? { area } : {}),
  ...(sort ? { sort } : {}),
});

export const fetchVisiChatHistory = createAsyncThunk(
  "usageStats/fetchVisiChatHistory",
  async (criteria) => {
    const response = await localhost.get(visiChatsUrl(criteria), {
      params: visiChatParams(criteria),
    });
    return {
      total: response.data?.total ?? 0,
      conversations: response.data?.conversations ?? [],
    };
  }
);

export const exportVisiChatHistoryThunk = createAsyncThunk(
  "usageStats/exportVisiChatHistory",
  async (criteria) => {
    const response = await localhost.get(`${visiChatsUrl(criteria)}/export`, {
      params: visiChatParams(criteria),
      responseType: "blob",
    });
    downloadFileFromResponse(response.data, VISI_CHATS_FILE_NAME, {
      type: CSV_MIME_TYPE,
    });
  }
);

const aiUsageParams = ({ filters, aiUsageUser }, details) =>
  details
    ? buildParams(details)
    : buildParams({ ...filters, user: aiUsageUser });

export const fetchAiUsageAvailability = createAsyncThunk(
  "usageStats/fetchAiUsageAvailability",
  async () => {
    const { data } = await localhost.get(`${AI_USAGE_URL}/availability`);
    return data;
  },
  {
    condition: (_, { getState }) =>
      getState().usageStats.aiUsageAvailabilityStatus !== "pending",
  }
);

const hasCompleteRange = (details, { getState }) =>
  Boolean(details) || !selectIsCustomRangeIncomplete(getState());

export const fetchAiUsage = createAsyncThunk(
  "usageStats/fetchAiUsage",
  async (details, { getState, rejectWithValue }) => {
    try {
      const { data } = await localhost.get(AI_USAGE_URL, {
        params: aiUsageParams(getState().usageStats, details),
      });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
  { condition: hasCompleteRange }
);

export const exportAiUsageThunk = createAsyncThunk(
  "usageStats/exportAiUsage",
  async (details, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await localhost.get(`${AI_USAGE_URL}/export`, {
        params: aiUsageParams(getState().usageStats, details),
        responseType: "blob",
      });
      downloadFileFromResponse(response.data, AI_USAGE_FILE_NAME, {
        type: CSV_MIME_TYPE,
      });
    } catch (error) {
      await dispatch(showDownloadErrorModal(error));
      return rejectWithValue(getErrorMessage(error));
    }
  },
  { condition: hasCompleteRange }
);

export const tokenUsageQueryKey = ({ range, from, to }, granularity) =>
  [range, from ?? "", to ?? "", granularity ?? "auto"].join("|");

const tokenUsageParams = ({ range, from, to }, granularity) =>
  buildParams({ range, from, to, granularity });

export const fetchTokenUsageTimeline = createAsyncThunk(
  "usageStats/fetchTokenUsageTimeline",
  async ({ period, granularity }, { rejectWithValue }) => {
    try {
      const { data } = await localhost.get(TOKEN_USAGE_URL, {
        params: tokenUsageParams(period, granularity),
      });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  },
  {
    condition: ({ period, granularity }, { getState }) =>
      !getState().usageStats.tokenUsage.queries[
        tokenUsageQueryKey(period, granularity)
      ]?.isLoading,
  }
);

export const exportTokenUsageThunk = createAsyncThunk(
  "usageStats/exportTokenUsage",
  async ({ period, granularity }, { dispatch, rejectWithValue }) => {
    try {
      const response = await localhost.get(`${TOKEN_USAGE_URL}/export`, {
        params: tokenUsageParams(period, granularity),
        responseType: "blob",
      });
      downloadFileFromResponse(
        response.data,
        `visiblethread-token-usage-${period.range}-${granularity}.csv`,
        { type: CSV_MIME_TYPE }
      );
    } catch (error) {
      await dispatch(showDownloadErrorModal(error));
      return rejectWithValue(getErrorMessage(error));
    }
  }
);
